import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useEffect, useMemo, useState } from "react";

const TOTAL_GAMES = 1000;
const BATCH_SIZE = 10;
const TOTAL_BATCHES = TOTAL_GAMES / BATCH_SIZE;
const MAX_SKIPPED = 100;

type BatchResult = {
  added: number;
  skipped: number;
  totalStored: number;
  fetchedCount: number;
  cooldownUntil?: number;
  lastRefreshAt?: number;
};

type StatusResponse = {
  cooldownUntil?: number;
  lastRefreshAt?: number;
  now?: number;
};

const formatDuration = (ms: number) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(" ");
};

export function ProplayFetcher() {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [addedTotal, setAddedTotal] = useState(0);
  const [skippedTotal, setSkippedTotal] = useState(0);
  const [storedTotal, setStoredTotal] = useState<number | null>(null);
  const [status, setStatus] = useState("Idle");
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(id);
  }, []);

  const cooldownRemainingMs = Math.max(0, cooldownUntil - now);
  const isCooldownActive = cooldownRemainingMs > 0;

  const cooldownText = useMemo(() => {
    if (!cooldownUntil) return "Cooldown: ready";
    if (!isCooldownActive) return "Cooldown: ready";
    return `Cooldown: ${formatDuration(cooldownRemainingMs)}`;
  }, [cooldownRemainingMs, cooldownUntil, isCooldownActive]);

  const lastRefreshText = useMemo(() => {
    if (!lastRefreshAt) return "Never";
    return new Date(lastRefreshAt).toLocaleString();
  }, [lastRefreshAt]);

  const loadStatus = async (): Promise<StatusResponse | null> => {
    try {
      const res = await fetch("/api/proplay/status");
      if (!res.ok) return null;

      const data = (await res.json()) as StatusResponse;
      if (typeof data.cooldownUntil === "number") {
        setCooldownUntil(data.cooldownUntil);
      }
      if (typeof data.lastRefreshAt === "number") {
        setLastRefreshAt(data.lastRefreshAt || null);
      }
      if (typeof data.now === "number") {
        setNow(data.now);
      }
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    void loadStatus();
  }, []);

  const fetchBatch = async (start: number): Promise<BatchResult> => {
    const res = await fetch("/api/proplay/batch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ start }),
    });

    if (!res.ok) {
      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        const data = (await res.json()) as {
          error?: string;
          cooldownUntil?: number;
          lastRefreshAt?: number;
        };
        if (typeof data.cooldownUntil === "number") {
          setCooldownUntil(data.cooldownUntil);
        }
        if (typeof data.lastRefreshAt === "number") {
          setLastRefreshAt(data.lastRefreshAt || null);
        }
        const message = typeof data?.error === "string" ? data.error : "Request failed";
        throw new Error(message);
      }

      const message = await res.text();
      throw new Error(message || "Request failed");
    }

    return (await res.json()) as BatchResult;
  };

  const runFetch = async () => {
    if (isRunning) return;

    setIsRunning(true);
    setStatus("Checking cooldown...");
    setError(null);

    const statusData = await loadStatus();
    const latestCooldownUntil = statusData?.cooldownUntil ?? cooldownUntil;
    if (latestCooldownUntil > Date.now()) {
      setStatus(`Cooldown active. Try again in ${formatDuration(latestCooldownUntil - Date.now())}.`);
      setIsRunning(false);
      return;
    }

    setProgress(0);
    setAddedTotal(0);
    setSkippedTotal(0);
    setStoredTotal(null);
    setStatus("Starting...");

    try {
      let addedSoFar = 0;
      let skippedSoFar = 0;
      let stoppedEarly = false;

      for (let batch = 0; batch < TOTAL_BATCHES; batch += 1) {
        const start = batch * BATCH_SIZE;
        const end = start + BATCH_SIZE - 1;
        setStatus(`Fetching games ${start}-${end}...`);

        const result = await fetchBatch(start);
        addedSoFar += result.added;
        skippedSoFar += result.skipped;
        setAddedTotal(addedSoFar);
        setSkippedTotal(skippedSoFar);
        setStoredTotal(result.totalStored);
        if (typeof result.cooldownUntil === "number") {
          setCooldownUntil(result.cooldownUntil);
        }
        if (typeof result.lastRefreshAt === "number") {
          setLastRefreshAt(result.lastRefreshAt || null);
        }
        setProgress(Math.round(((batch + 1) / TOTAL_BATCHES) * 100));

        if (skippedSoFar >= MAX_SKIPPED) {
          stoppedEarly = true;
          setStatus(`Stopped after skipping ${skippedSoFar} games.`);
          break;
        }
      }

      if (!stoppedEarly) {
        setStatus("Done. Saved to data/proplay-games.json");
      }
    } catch (err) {
      setError(String(err));
      setStatus("Failed");
      await loadStatus();
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={runFetch} disabled={isRunning || isCooldownActive}>
          {isRunning ? "Fetching..." : "Fetch last 1000 games"}
        </Button>
        <span className="text-sm text-muted-foreground">{status}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {cooldownText} · Last refreshed: {lastRefreshText}
      </div>
      <Progress value={progress} className="h-2" />
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-muted-foreground">Added</div>
          <div className="font-medium">{addedTotal}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Skipped</div>
          <div className="font-medium">{skippedTotal}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Stored</div>
          <div className="font-medium">{storedTotal ?? "-"}</div>
        </div>
      </div>
      {error ? <div className="text-sm text-destructive">{error}</div> : null}
    </div>
  );
}
